import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Download, Save, Check, X, Plus, Trash2, AlertTriangle, Loader2, RefreshCw, Upload, Pen } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./styles/documentation-page.css";
import inverterCheckSheetApi, { InverterCheckSheetData, InverterChecklistItem } from "./inverterCheckSheetApi";

// Types
interface InverterSerial {
  inv01: string;
  inv02: string;
  inv03: string;
  inv04: string;
}

interface ChecklistItem {
  id: number;
  sno: number;
  description: string;
  finding: string;
  inv01: string;
  inv02: string;
  inv03: string;
  inv04: string;
  remarks: string;
}

interface SignatureField {
  name: string;
  signature: string;
}

interface FormData {
  equipment: string;
  frequency: string;
  customerName: string;
  date: string;
  blockNo: string;
  siteName: string;
  inverterSerials: InverterSerial;
  checklistItems: ChecklistItem[];
  comments: string;
  checkBy: SignatureField;
  verifiedBy: SignatureField;
  siteManagerContractor: SignatureField;
  siteManagerOwner: SignatureField;
}

// Default checklist items based on the document
const DEFAULT_CHECKLIST_ITEMS: ChecklistItem[] = [
  { id: 1, sno: 1, description: "Dust Filter Cleaning", finding: "Clean/ Unclean", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 2, sno: 2, description: "Cleanliness of Functional Components", finding: "Clean/ Unclean", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 3, sno: 3, description: "Inspection of hole sealing AC and DC side", finding: "OK/ NOT OK", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 4, sno: 4, description: "LCD Display", finding: "OK/ NOT OK", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 5, sno: 5, description: "Communication with SCADA", finding: "OK/ NOT OK", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 6, sno: 6, description: "Door Operation, Hinges and Stoppers", finding: "OK/ NOT OK", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 7, sno: 7, description: "Energy Produced Today", finding: "OK/ NOT OK", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 8, sno: 8, description: "Panel Earthing", finding: "OK/ NOT OK", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 9, sno: 9, description: "AC and DCSPD", finding: "OK/ NOT OK", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 10, sno: 10, description: "Fuse", finding: "OK/ NOT OK", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 11, sno: 11, description: "Switch Cabinet", finding: "OK/ NOT OK", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 12, sno: 12, description: "Hot Spot", finding: "YES/ NO", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
  { id: 13, sno: 13, description: "Fans in Operation", finding: "YES/ NO", inv01: "", inv02: "", inv03: "", inv04: "", remarks: "" },
];

const InverterCheckSheet: React.FC = () => {
  const { site } = useParams<{ site: string }>();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [currentRecordId, setCurrentRecordId] = useState<number | null>(null);
  const [existingRecords, setExistingRecords] = useState<InverterCheckSheetData[]>([]);
  
  // Signature states
  const [activeSignatureField, setActiveSignatureField] = useState<'checkBy' | 'verifiedBy' | 'siteManagerContractor' | 'siteManagerOwner' | null>(null);
  const [signatureMode, setSignatureMode] = useState<'draw' | 'upload'>('draw');
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signatureFileInputRef = useRef<HTMLInputElement>(null);

  // Form data state
  const [formData, setFormData] = useState<FormData>({
    equipment: "Central Inverter",
    frequency: "Monthly",
    customerName: "",
    date: new Date().toISOString().split('T')[0],
    blockNo: "",
    siteName: "AxonMetric Global Private Limited",
    inverterSerials: {
      inv01: "",
      inv02: "",
      inv03: "",
      inv04: ""
    },
    checklistItems: [...DEFAULT_CHECKLIST_ITEMS],
    comments: "",
    checkBy: { name: "", signature: "" },
    verifiedBy: { name: "", signature: "" },
    siteManagerContractor: { name: "", signature: "" },
    siteManagerOwner: { name: "", signature: "" }
  });

  // Format metadata
  const formatMetadata = {
    formatNumber: "AxM-EQP-01-25",
    revisionNumber: "01",
    effectiveFrom: "17 DEC 25"
  };

  // Load existing records for the site
  const loadExistingRecords = useCallback(async () => {
    if (!site) return;
    
    try {
      const response = await inverterCheckSheetApi.getAll(site);
      if (response.success && response.data) {
        setExistingRecords(response.data);
      }
    } catch (err) {
      console.error("Failed to load existing records:", err);
    }
  }, [site]);

  // Load a specific record by ID
  const loadRecord = async (id: number) => {
    setLoading(true);
    setError("");
    
    try {
      const response = await inverterCheckSheetApi.getById(id, site);
      if (response.success && response.data) {
        const record = response.data;
        setCurrentRecordId(record.id || null);
        
        // Map API data to form data
        setFormData({
          equipment: record.equipment || "Central Inverter",
          frequency: record.frequency || "Monthly",
          customerName: record.customer_name || "",
          date: record.check_date,
          blockNo: record.block_no || "",
          siteName: record.site_name || "AxonMetric Global Private Limited",
          inverterSerials: {
            inv01: record.inverter_serial_01 || "",
            inv02: record.inverter_serial_02 || "",
            inv03: record.inverter_serial_03 || "",
            inv04: record.inverter_serial_04 || ""
          },
          checklistItems: record.checklist_items?.map((item, index) => ({
            id: item.id || index + 1,
            sno: item.serial_number || index + 1,
            description: item.description,
            finding: item.finding || "OK/ NOT OK",
            inv01: item.inv_01_status || "",
            inv02: item.inv_02_status || "",
            inv03: item.inv_03_status || "",
            inv04: item.inv_04_status || "",
            remarks: item.remarks || ""
          })) || [...DEFAULT_CHECKLIST_ITEMS],
          comments: record.comments || "",
          checkBy: { name: record.check_by_name || "", signature: record.check_by_signature || "" },
          verifiedBy: { name: record.verified_by_name || "", signature: record.verified_by_signature || "" },
          siteManagerContractor: { name: record.site_manager_contractor_name || "", signature: record.site_manager_contractor_signature || "" },
          siteManagerOwner: { name: record.site_manager_owner_name || "", signature: record.site_manager_owner_signature || "" }
        });
        
        setSuccessMessage("Record loaded successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.error || "Failed to load record");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load record");
    } finally {
      setLoading(false);
    }
  };

  // Create new check sheet (reset form)
  const handleNewSheet = () => {
    setCurrentRecordId(null);
    setFormData({
      equipment: "Central Inverter",
      frequency: "Monthly",
      customerName: "",
      date: new Date().toISOString().split('T')[0],
      blockNo: "",
      siteName: "AxonMetric Global Private Limited",
      inverterSerials: {
        inv01: "",
        inv02: "",
        inv03: "",
        inv04: ""
      },
      checklistItems: [...DEFAULT_CHECKLIST_ITEMS],
      comments: "",
      checkBy: { name: "", signature: "" },
      verifiedBy: { name: "", signature: "" },
      siteManagerContractor: { name: "", signature: "" },
      siteManagerOwner: { name: "", signature: "" }
    });
    setSuccessMessage("New check sheet created!");
    setTimeout(() => setSuccessMessage(""), 2000);
  };

  // Load records on mount
  useEffect(() => {
    loadExistingRecords();
  }, [loadExistingRecords]);

  // Calculate progress
  const completedCount = formData.checklistItems.filter(item => 
    item.inv01 || item.inv02 || item.inv03 || item.inv04
  ).length;
  const totalCount = formData.checklistItems.length;
  const progressPercentage = (completedCount / totalCount) * 100;

  // Handle form field changes
  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle inverter serial changes
  const handleSerialChange = (field: keyof InverterSerial, value: string) => {
    setFormData(prev => ({
      ...prev,
      inverterSerials: { ...prev.inverterSerials, [field]: value }
    }));
  };

  // Handle checklist item changes
  const handleChecklistChange = (id: number, field: keyof ChecklistItem, value: string) => {
    setFormData(prev => ({
      ...prev,
      checklistItems: prev.checklistItems.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  // Handle signature field changes
  const handleSignatureChange = (field: 'checkBy' | 'verifiedBy' | 'siteManagerContractor' | 'siteManagerOwner', subField: 'name' | 'signature', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: { ...prev[field], [subField]: value }
    }));
  };

  // Get finding options based on item
  const getFindingOptions = (finding: string): string[] => {
    if (finding.includes("Clean")) return ["Clean", "Unclean"];
    if (finding.includes("YES")) return ["YES", "NO"];
    return ["OK", "NOT OK"];
  };

  // Add new checklist item
  const addChecklistItem = () => {
    const newId = Math.max(...formData.checklistItems.map(i => i.id)) + 1;
    const newSno = formData.checklistItems.length + 1;
    setFormData(prev => ({
      ...prev,
      checklistItems: [
        ...prev.checklistItems,
        {
          id: newId,
          sno: newSno,
          description: "",
          finding: "OK/ NOT OK",
          inv01: "",
          inv02: "",
          inv03: "",
          inv04: "",
          remarks: ""
        }
      ]
    }));
  };

  // Remove checklist item
  const removeChecklistItem = (id: number) => {
    if (formData.checklistItems.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      checklistItems: prev.checklistItems
        .filter(item => item.id !== id)
        .map((item, index) => ({ ...item, sno: index + 1 }))
    }));
  };

  // Save form data
  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      // Prepare data for API
      const apiData: InverterCheckSheetData = {
        site_code: site,
        format_number: formatMetadata.formatNumber,
        revision_number: formatMetadata.revisionNumber,
        effective_from: formatMetadata.effectiveFrom,
        check_date: formData.date,
        equipment: formData.equipment,
        frequency: formData.frequency,
        customer_name: formData.customerName,
        block_no: formData.blockNo,
        site_name: formData.siteName,
        inverter_serial_01: formData.inverterSerials.inv01,
        inverter_serial_02: formData.inverterSerials.inv02,
        inverter_serial_03: formData.inverterSerials.inv03,
        inverter_serial_04: formData.inverterSerials.inv04,
        comments: formData.comments,
        check_by_name: formData.checkBy.name,
        check_by_signature: formData.checkBy.signature,
        verified_by_name: formData.verifiedBy.name,
        verified_by_signature: formData.verifiedBy.signature,
        site_manager_contractor_name: formData.siteManagerContractor.name,
        site_manager_contractor_signature: formData.siteManagerContractor.signature,
        site_manager_owner_name: formData.siteManagerOwner.name,
        site_manager_owner_signature: formData.siteManagerOwner.signature,
        checklist_items: formData.checklistItems.map(item => ({
          serial_number: item.sno,
          description: item.description,
          finding: item.finding,
          inv_01_status: item.inv01,
          inv_02_status: item.inv02,
          inv_03_status: item.inv03,
          inv_04_status: item.inv04,
          remarks: item.remarks
        }))
      };

      let response;
      if (currentRecordId) {
        // Update existing record
        response = await inverterCheckSheetApi.update(currentRecordId, apiData, site);
      } else {
        // Create new record
        response = await inverterCheckSheetApi.create(apiData, site);
      }

      if (response.success) {
        if (response.data?.id) {
          setCurrentRecordId(response.data.id);
        }
        setSuccessMessage(response.message || "Inverter Check Sheet saved successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
        // Reload records list
        loadExistingRecords();
      } else {
        setError(response.error || "Failed to save. Please try again.");
      }
    } catch (err: any) {
      setError(err.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Format date as "DD MON YY" (e.g., "17 DEC 25")
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear().toString().slice(-2);
      return `${day} ${month} ${year}`;
    } catch (e) {
      return dateString;
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let yPos = margin;

    // Colors
    const headerBgColor: [number, number, number] = [240, 240, 240];
    const borderColor: [number, number, number] = [0, 0, 0];
    const textColor: [number, number, number] = [0, 0, 0];
    const grayTextColor: [number, number, number] = [128, 128, 128];

    // Helper function to draw bordered cell with multi-line support
    const drawCell = (x: number, y: number, width: number, height: number, text: string | string[], options: {
      align?: 'left' | 'center' | 'right';
      fontSize?: number;
      fontStyle?: 'normal' | 'bold';
      bgColor?: [number, number, number];
      verticalAlign?: 'top' | 'middle';
      textColor?: [number, number, number];
      lineHeight?: number;
    } = {}) => {
      const { 
        align = 'left', 
        fontSize = 8, 
        fontStyle = 'normal', 
        bgColor, 
        verticalAlign = 'middle',
        textColor: cellTextColor = textColor,
        lineHeight = fontSize * 1.2
      } = options;
      
      // Draw background if specified
      if (bgColor) {
        doc.setFillColor(...bgColor);
        doc.rect(x, y, width, height, 'F');
      }
      
      // Draw border
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.2);
      doc.rect(x, y, width, height, 'S');
      
      // Draw text
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', fontStyle);
      doc.setTextColor(...cellTextColor);
      
      const lines = Array.isArray(text) ? text : [text];
      const padding = 2;
      
      // Calculate starting Y position based on vertical alignment
      let startY: number;
      if (verticalAlign === 'top') {
        startY = y + padding + fontSize / 2;
      } else {
        // middle alignment
        const totalTextHeight = (lines.length - 1) * lineHeight + fontSize;
        startY = y + (height - totalTextHeight) / 2 + fontSize / 2;
      }
      
      // Draw each line
      lines.forEach((line, index) => {
        let textX = x + padding;
        if (align === 'center') textX = x + width / 2;
        if (align === 'right') textX = x + width - padding;
        
        const textY = startY + (index * lineHeight);
        doc.text(line, textX, textY, { align });
      });
    };

    // === HEADER SECTION ===
    const headerHeight = 20;
    const logoWidth = 50;
    const titleWidth = 70;
    const infoTableWidth = pageWidth - margin - logoWidth - titleWidth - margin;
    
    // Company Logo placeholder (gray text)
    drawCell(margin, yPos, logoWidth, headerHeight, "COMPANY LOGO", { 
      align: 'center', 
      fontSize: 8, 
      bgColor: headerBgColor,
      textColor: grayTextColor,
      verticalAlign: 'middle'
    });
    
    // Title (gray text, bold)
    drawCell(margin + logoWidth, yPos, titleWidth, headerHeight, ["INVERTER CHECK SHEET", "MONTHLY"], { 
      align: 'center', 
      fontSize: 10, 
      fontStyle: 'bold',
      textColor: grayTextColor,
      verticalAlign: 'middle',
      lineHeight: 5
    });
    
    // Format info table (right side)
    const infoX = margin + logoWidth + titleWidth;
    const infoWidth1 = infoTableWidth * 0.58; // ~58% for labels
    const infoWidth2 = infoTableWidth * 0.42; // ~42% for values
    const infoRowHeight = headerHeight / 3;
    
    // Row 1: FORMAT #
    drawCell(infoX, yPos, infoWidth1, infoRowHeight, "FORMAT #", { 
      fontSize: 7, 
      bgColor: headerBgColor,
      align: 'left'
    });
    drawCell(infoX + infoWidth1, yPos, infoWidth2, infoRowHeight, formatMetadata.formatNumber, { 
      fontSize: 7, 
      align: 'left'
    });
    
    // Row 2: REVISION #
    drawCell(infoX, yPos + infoRowHeight, infoWidth1, infoRowHeight, "REVISION #", { 
      fontSize: 7, 
      bgColor: headerBgColor,
      align: 'left'
    });
    drawCell(infoX + infoWidth1, yPos + infoRowHeight, infoWidth2, infoRowHeight, formatMetadata.revisionNumber, { 
      fontSize: 7, 
      align: 'center'
    });
    
    // Row 3: EFFECTIVED FROM
    drawCell(infoX, yPos + infoRowHeight * 2, infoWidth1, infoRowHeight, "EFFECTIVED FROM", { 
      fontSize: 7, 
      bgColor: headerBgColor,
      align: 'left'
    });
    drawCell(infoX + infoWidth1, yPos + infoRowHeight * 2, infoWidth2, infoRowHeight, formatMetadata.effectiveFrom, { 
      fontSize: 7, 
      align: 'left'
    });

    yPos += headerHeight + 5;

    // === FORM INFORMATION SECTION ===
    const formRowHeight = 8;
    const labelColWidth = 35;  // Width for labels (Equipment, Customer Name, etc.)
    const valueCol1Width = 40; // First value column
    const labelCol2Width = 30; // Second label column (Frequency, Date, Site Name)
    const valueCol2Width = pageWidth - 2 * margin - labelColWidth - valueCol1Width - labelCol2Width; // Remaining width

    // Row 1: Equipment, Frequency
    drawCell(margin, yPos, labelColWidth, formRowHeight, "Equipment", { fontStyle: 'bold', fontSize: 8, align: 'left' });
    drawCell(margin + labelColWidth, yPos, valueCol1Width, formRowHeight, formData.equipment, { fontSize: 8, align: 'left' });
    drawCell(margin + labelColWidth + valueCol1Width, yPos, labelCol2Width, formRowHeight, "Frequency", { fontStyle: 'bold', fontSize: 8, align: 'left' });
    drawCell(margin + labelColWidth + valueCol1Width + labelCol2Width, yPos, valueCol2Width, formRowHeight, formData.frequency, { fontSize: 8, align: 'left' });
    yPos += formRowHeight;

    // Row 2: Customer Name, Date (formatted as "DD MON YY")
    const formattedDate = formatDate(formData.date);
    drawCell(margin, yPos, labelColWidth, formRowHeight, "Customer Name", { fontStyle: 'bold', fontSize: 8, align: 'left' });
    drawCell(margin + labelColWidth, yPos, valueCol1Width, formRowHeight, formData.customerName, { fontSize: 8, align: 'left' });
    drawCell(margin + labelColWidth + valueCol1Width, yPos, labelCol2Width, formRowHeight, "Date", { fontStyle: 'bold', fontSize: 8, align: 'left' });
    drawCell(margin + labelColWidth + valueCol1Width + labelCol2Width, yPos, valueCol2Width, formRowHeight, formattedDate, { fontSize: 8, align: 'left' });
    yPos += formRowHeight;

    // Row 3: Block No, Site Name
    drawCell(margin, yPos, labelColWidth, formRowHeight, "Block No", { fontStyle: 'bold', fontSize: 8, align: 'left' });
    drawCell(margin + labelColWidth, yPos, valueCol1Width, formRowHeight, formData.blockNo, { fontSize: 8, align: 'left' });
    drawCell(margin + labelColWidth + valueCol1Width, yPos, labelCol2Width, formRowHeight, "Site Name", { fontStyle: 'bold', fontSize: 8, align: 'left' });
    drawCell(margin + labelColWidth + valueCol1Width + labelCol2Width, yPos, valueCol2Width, formRowHeight, formData.siteName, { fontSize: 8, align: 'left' });
    yPos += formRowHeight;

    // Row 4: Inverter Serial no (single merged box with 4 lines in two-column layout)
    const serialLabelWidth = 35;
    const serialBoxWidth = pageWidth - 2 * margin - serialLabelWidth;
    const serialBoxHeight = formRowHeight * 1.5; // Taller box for 4 lines
    
    drawCell(margin, yPos, serialLabelWidth, serialBoxHeight, "Inverter Serial no", { 
      fontStyle: 'bold', 
      fontSize: 8, 
      align: 'left',
      verticalAlign: 'top'
    });
    
    // Draw the merged box border first
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.2);
    doc.rect(margin + serialLabelWidth, yPos, serialBoxWidth, serialBoxHeight, 'S');
    
    // Draw inverter serial numbers in two-column layout
    const leftColumnX = margin + serialLabelWidth + 2;
    const rightColumnX = margin + serialLabelWidth + serialBoxWidth / 2 + 2;
    const lineSpacing = 3.5;
    const startY = yPos + 2 + 7 / 2; // Top padding + half font size
    
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textColor);
    
    // Left column: INV-01 and INV-02
    doc.text(`INV-01: ${formData.inverterSerials.inv01 || ''}`, leftColumnX, startY, { align: 'left' });
    doc.text(`INV-02: ${formData.inverterSerials.inv02 || ''}`, leftColumnX, startY + lineSpacing, { align: 'left' });
    
    // Right column: INV-03 and INV-04
    doc.text(`INV-03: ${formData.inverterSerials.inv03 || ''}`, rightColumnX, startY, { align: 'left' });
    doc.text(`INV-04: ${formData.inverterSerials.inv04 || ''}`, rightColumnX, startY + lineSpacing, { align: 'left' });
    
    yPos += serialBoxHeight + 5;

    // === CHECKLIST TABLE ===
    const tableData = formData.checklistItems.map(item => [
      item.sno.toString(),
      item.description,
      item.finding,
      item.inv01,
      item.inv02,
      item.inv03,
      item.inv04,
      item.remarks
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [
        [
          { content: 'SNO', styles: { halign: 'center' } },
          { content: 'DESCRIPTION', styles: { halign: 'center' } },
          { content: 'FINDING', styles: { halign: 'center' } },
          { content: 'INV 01', styles: { halign: 'center' } },
          { content: 'INV 02', styles: { halign: 'center' } },
          { content: 'INV 03', styles: { halign: 'center' } },
          { content: 'INV 04', styles: { halign: 'center' } },
          { content: 'REMARKS', styles: { halign: 'center' } }
        ]
      ],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 7,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        textColor: [0, 0, 0]
      },
      headStyles: {
        fillColor: [200, 200, 200],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 50 },
        2: { cellWidth: 25, halign: 'center' },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' },
        5: { cellWidth: 18, halign: 'center' },
        6: { cellWidth: 18, halign: 'center' },
        7: { cellWidth: 'auto' }
      },
      margin: { left: margin, right: margin }
    });

    // Get final Y position after table
    yPos = (doc as any).lastAutoTable.finalY + 5;

    // === COMMENTS SECTION ===
    const commentsHeight = 20;
    drawCell(margin, yPos, pageWidth - 2 * margin, 6, "Comments:", { fontStyle: 'bold', fontSize: 8 });
    yPos += 6;
    drawCell(margin, yPos, pageWidth - 2 * margin, commentsHeight, formData.comments, { fontSize: 8, verticalAlign: 'top' });
    yPos += commentsHeight + 10;

    // === SIGNATURE SECTION ===
    const sigWidth = (pageWidth - 2 * margin) / 4;
    const sigHeight = 20;
    const sigSectionHeight = 8 + sigHeight; // Header + signature box
    const bottomMargin = 15; // Extra margin from page bottom

    // Position signature boxes at the bottom of the page
    const sigYPos = pageHeight - bottomMargin - sigSectionHeight;

    // Check if we need a new page for signatures
    if (yPos + sigSectionHeight + 10 > pageHeight - margin) {
      doc.addPage();
      yPos = margin; // Reset yPos for new page
    }

    // Use bottom position if there's enough space, otherwise use current position
    const finalSigYPos = (yPos + sigSectionHeight + 10 <= sigYPos) ? sigYPos : yPos;

    // Signature headers
    drawCell(margin, finalSigYPos, sigWidth, 8, "Check By:", { fontStyle: 'bold', fontSize: 8, align: 'center', bgColor: headerBgColor });
    drawCell(margin + sigWidth, finalSigYPos, sigWidth, 8, "Verified By:", { fontStyle: 'bold', fontSize: 8, align: 'center', bgColor: headerBgColor });
    drawCell(margin + sigWidth * 2, finalSigYPos, sigWidth, 8, "Site Manager (Contractor)", { fontStyle: 'bold', fontSize: 8, align: 'center', bgColor: headerBgColor });
    drawCell(margin + sigWidth * 3, finalSigYPos, sigWidth, 8, "Site Manager (Owner)", { fontStyle: 'bold', fontSize: 8, align: 'center', bgColor: headerBgColor });

    // Signature boxes with names and signature images
    const addSignatureToBox = (x: number, y: number, width: number, height: number, name: string, signature: string) => {
      // Draw the box border
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.2);
      doc.rect(x, y, width, height, 'S');
      
      // Add signature image if available
      if (signature) {
        try {
          // Calculate signature image dimensions
          const sigImageWidth = width * 0.8; // 80% of box width
          const sigImageHeight = height * 0.6; // 60% of box height
          const sigX = x + (width - sigImageWidth) / 2; // Center horizontally
          const sigY = y + 2; // Top padding
          
          // Add signature image
          doc.addImage(signature, 'PNG', sigX, sigY, sigImageWidth, sigImageHeight);
          
          // Add name below signature
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...textColor);
          const nameY = y + sigImageHeight + 4; // Below signature
          doc.text(name, x + width / 2, nameY, { align: 'center' });
        } catch (error) {
          // If image fails, just show name
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...textColor);
          doc.text(name, x + width / 2, y + height / 2, { align: 'center' });
        }
      } else {
        // Just show name if no signature
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...textColor);
        doc.text(name, x + width / 2, y + height / 2, { align: 'center' });
      }
    };

    // Add signature boxes with images
    addSignatureToBox(margin, finalSigYPos + 8, sigWidth, sigHeight, formData.checkBy.name, formData.checkBy.signature);
    addSignatureToBox(margin + sigWidth, finalSigYPos + 8, sigWidth, sigHeight, formData.verifiedBy.name, formData.verifiedBy.signature);
    addSignatureToBox(margin + sigWidth * 2, finalSigYPos + 8, sigWidth, sigHeight, formData.siteManagerContractor.name, formData.siteManagerContractor.signature);
    addSignatureToBox(margin + sigWidth * 3, finalSigYPos + 8, sigWidth, sigHeight, formData.siteManagerOwner.name, formData.siteManagerOwner.signature);

    // Save the PDF
    const fileName = `Inverter_CheckSheet_${formData.date}_${site || 'AXON'}.pdf`;
    doc.save(fileName);
    
    setSuccessMessage(`PDF exported successfully as ${fileName}`);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  // Signature handling functions
  const handleSignatureClick = (field: 'checkBy' | 'verifiedBy' | 'siteManagerContractor' | 'siteManagerOwner') => {
    setActiveSignatureField(field);
  };

  const closeSignatureModal = () => {
    setActiveSignatureField(null);
    clearSignature();
  };

  // Helper function to get correct canvas coordinates
  const getCanvasCoordinates = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
    canvas: HTMLCanvasElement
  ) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    return { x, y };
  };

  // Signature Drawing Functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault(); // Prevent scrolling on touch devices
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCanvasCoordinates(e, canvas);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 1.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = () => {
    if (!canvasRef.current || !activeSignatureField) return;
    
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    
    setFormData(prev => ({
      ...prev,
      [activeSignatureField]: {
        ...prev[activeSignatureField],
        signature: signatureData
      }
    }));
    
    closeSignatureModal();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeSignatureField) return;

    if (!file.type.startsWith('image/')) {
      setError("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target?.result as string;
      
      setFormData(prev => ({
        ...prev,
        [activeSignatureField]: {
          ...prev[activeSignatureField],
          signature: imageData
        }
      }));
      
      closeSignatureModal();
    };
    reader.readAsDataURL(file);
  };

  // Initialize canvas on mount and resize
  useEffect(() => {
    const initCanvas = () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        const rect = canvas.getBoundingClientRect();
        const displayWidth = rect.width;
        const displayHeight = 200;
        
        const dpr = window.devicePixelRatio || 1;
        canvas.width = displayWidth * dpr;
        canvas.height = displayHeight * dpr;
        
        ctx.scale(dpr, dpr);
        
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    };

    const timer = setTimeout(initCanvas, 100);
    window.addEventListener('resize', initCanvas);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', initCanvas);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 pt-16 pb-2 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-[#0066A3]" />
          <p className="text-sm text-gray-600">Loading check sheet...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="documentation-page min-h-screen bg-gray-100 pt-16 pb-4"
      style={{ fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        {/* Error/Success Messages */}
        {error && (
          <div className="mb-2 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertTriangle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-800">{error}</p>
            <button onClick={() => setError("")} className="ml-auto text-red-600 hover:text-red-800">
              <X size={14} />
            </button>
          </div>
        )}
        {successMessage && (
          <div className="mb-2 p-2.5 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
            <Check size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-green-800">{successMessage}</p>
            <button onClick={() => setSuccessMessage("")} className="ml-auto text-green-600 hover:text-green-800">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Header Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900 mb-0.5">
                INVERTER CHECK SHEET - Monthly
                {currentRecordId && (
                  <span className="ml-2 text-sm font-normal text-[#0066A3]">(Editing Record #{currentRecordId})</span>
                )}
              </h1>
              <p className="text-xs text-gray-600">
                FORMAT #: {formatMetadata.formatNumber} | REV: {formatMetadata.revisionNumber} | Effective: {formatMetadata.effectiveFrom}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleNewSheet}
                className="documentation-action-btn flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition-colors"
                style={{ fontWeight: 500 }}
              >
                <Plus size={16} />
                New Sheet
              </button>
              <button
                onClick={loadExistingRecords}
                className="documentation-action-btn flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition-colors"
                style={{ fontWeight: 500 }}
                title="Refresh records list"
              >
                <RefreshCw size={16} />
              </button>
              <button
                onClick={handleExportPDF}
                className="documentation-action-btn flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm transition-colors"
                style={{ fontWeight: 600 }}
              >
                <Download size={16} />
                Export PDF
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="documentation-action-btn flex items-center gap-1.5 px-4 py-2 bg-[#0066A3] hover:bg-[#1C4D88] text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ fontWeight: 600 }}
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {currentRecordId ? 'Update Sheet' : 'Save Sheet'}
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Existing Records Dropdown */}
          {existingRecords.length > 0 && (
            <div className="mb-3 p-2 bg-[#F2F8FC] rounded-lg">
              <label className="block text-xs font-medium text-[#1C4D88] mb-1">Load Existing Record:</label>
              <select
                onChange={(e) => {
                  const id = parseInt(e.target.value);
                  if (id) loadRecord(id);
                }}
                value={currentRecordId || ""}
                className="w-full sm:w-auto px-3 py-2 text-sm border border-[#C2DFF1] rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white cursor-pointer"
              >
                <option value="">-- Select a record --</option>
                {existingRecords.map((record) => (
                  <option key={record.id} value={record.id}>
                    {record.check_date} - {record.customer_name || 'No Customer'} - Block: {record.block_no || 'N/A'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Progress Bar */}
          <div className="mt-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-medium text-gray-700">
                {completedCount}/{totalCount} items checked
              </span>
              <span className="text-xs text-gray-500">{Math.round(progressPercentage)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-[#0066A3] h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Form Information Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Form Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Equipment</label>
              <input
                type="text"
                value={formData.equipment}
                onChange={(e) => handleFieldChange('equipment', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Frequency</label>
              <select
                value={formData.frequency}
                onChange={(e) => handleFieldChange('frequency', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white cursor-pointer"
              >
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Yearly">Yearly</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Customer Name</label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => handleFieldChange('customerName', e.target.value)}
                placeholder="Enter customer name"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => handleFieldChange('date', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Block No</label>
              <input
                type="text"
                value={formData.blockNo}
                onChange={(e) => handleFieldChange('blockNo', e.target.value)}
                placeholder="Enter block number"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">Site Name</label>
              <input
                type="text"
                value={formData.siteName}
                onChange={(e) => handleFieldChange('siteName', e.target.value)}
                placeholder="Enter site name"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
              />
            </div>
          </div>

          {/* Inverter Serial Numbers */}
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-700 mb-2">Inverter Serial Numbers</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">INV-01</label>
                <input
                  type="text"
                  value={formData.inverterSerials.inv01}
                  onChange={(e) => handleSerialChange('inv01', e.target.value)}
                  placeholder="Serial number"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">INV-02</label>
                <input
                  type="text"
                  value={formData.inverterSerials.inv02}
                  onChange={(e) => handleSerialChange('inv02', e.target.value)}
                  placeholder="Serial number"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">INV-03</label>
                <input
                  type="text"
                  value={formData.inverterSerials.inv03}
                  onChange={(e) => handleSerialChange('inv03', e.target.value)}
                  placeholder="Serial number"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">INV-04</label>
                <input
                  type="text"
                  value={formData.inverterSerials.inv04}
                  onChange={(e) => handleSerialChange('inv04', e.target.value)}
                  placeholder="Serial number"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Checklist Table Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3 overflow-hidden">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-base font-semibold text-gray-900">Checklist</h2>
            <button
              onClick={addChecklistItem}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F2F8FC] hover:bg-[#E3F0F9] text-[#1C4D88] rounded-lg text-xs font-medium transition-colors"
            >
              <Plus size={14} />
              Add Item
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-2 py-2 text-center font-semibold text-xs w-12">SNO</th>
                  <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-xs min-w-[200px]">DESCRIPTION</th>
                  <th className="border border-gray-300 px-2 py-2 text-center font-semibold text-xs w-24">FINDING</th>
                  <th className="border border-gray-300 px-2 py-2 text-center font-semibold text-xs w-20">INV 01</th>
                  <th className="border border-gray-300 px-2 py-2 text-center font-semibold text-xs w-20">INV 02</th>
                  <th className="border border-gray-300 px-2 py-2 text-center font-semibold text-xs w-20">INV 03</th>
                  <th className="border border-gray-300 px-2 py-2 text-center font-semibold text-xs w-20">INV 04</th>
                  <th className="border border-gray-300 px-2 py-2 text-left font-semibold text-xs min-w-[120px]">REMARKS</th>
                  <th className="border border-gray-300 px-2 py-2 text-center font-semibold text-xs w-12"></th>
                </tr>
              </thead>
              <tbody>
                {formData.checklistItems.map((item) => {
                  const options = getFindingOptions(item.finding);
                  return (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="border border-gray-300 px-2 py-1.5 text-center text-sm font-medium bg-gray-50">
                        {item.sno}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleChecklistChange(item.id, 'description', e.target.value)}
                          className="w-full px-2 py-1 text-sm border-0 focus:ring-1 focus:ring-[#0084CF] rounded bg-transparent"
                        />
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-center text-xs text-gray-600">
                        {item.finding}
                      </td>
                      {['inv01', 'inv02', 'inv03', 'inv04'].map((inv) => (
                        <td key={inv} className="border border-gray-300 px-1 py-1.5 text-center">
                          <select
                            value={item[inv as keyof ChecklistItem] as string}
                            onChange={(e) => handleChecklistChange(item.id, inv as keyof ChecklistItem, e.target.value)}
                            className="w-full px-1 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-[#0084CF] bg-white cursor-pointer"
                          >
                            <option value="">-</option>
                            {options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </td>
                      ))}
                      <td className="border border-gray-300 px-2 py-1.5">
                        <input
                          type="text"
                          value={item.remarks}
                          onChange={(e) => handleChecklistChange(item.id, 'remarks', e.target.value)}
                          placeholder="Remarks..."
                          className="w-full px-2 py-1 text-sm border-0 focus:ring-1 focus:ring-[#0084CF] rounded bg-transparent"
                        />
                      </td>
                      <td className="border border-gray-300 px-1 py-1.5 text-center">
                        <button
                          onClick={() => removeChecklistItem(item.id)}
                          className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Remove item"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Comments Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
          <h2 className="text-base font-semibold text-gray-900 mb-2">Comments:</h2>
          <textarea
            value={formData.comments}
            onChange={(e) => handleFieldChange('comments', e.target.value)}
            placeholder="Enter any additional comments..."
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] resize-none bg-white"
          />
        </div>

        {/* Signature Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Signatures</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-gray-200 rounded-lg p-3">
              <label className="block text-xs font-medium text-gray-700 mb-2">Check By:</label>
              <input
                type="text"
                value={formData.checkBy.name}
                onChange={(e) => handleSignatureChange('checkBy', 'name', e.target.value)}
                placeholder="Enter name"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white mb-2"
              />
              <button
                onClick={() => handleSignatureClick('checkBy')}
                className="w-full px-3 py-2 bg-[#F2F8FC] hover:bg-[#E3F0F9] text-[#1C4D88] rounded-lg text-xs font-medium transition-colors border border-[#C2DFF1]"
              >
                {formData.checkBy.signature ? 'Change Signature' : 'Add Signature'}
              </button>
              {formData.checkBy.signature && (
                <img
                  src={formData.checkBy.signature}
                  alt="Check By Signature"
                  className="mt-2 max-w-full h-16 border border-gray-200 rounded"
                />
              )}
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <label className="block text-xs font-medium text-gray-700 mb-2">Verified By:</label>
              <input
                type="text"
                value={formData.verifiedBy.name}
                onChange={(e) => handleSignatureChange('verifiedBy', 'name', e.target.value)}
                placeholder="Enter name"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white mb-2"
              />
              <button
                onClick={() => handleSignatureClick('verifiedBy')}
                className="w-full px-3 py-2 bg-[#F2F8FC] hover:bg-[#E3F0F9] text-[#1C4D88] rounded-lg text-xs font-medium transition-colors border border-[#C2DFF1]"
              >
                {formData.verifiedBy.signature ? 'Change Signature' : 'Add Signature'}
              </button>
              {formData.verifiedBy.signature && (
                <img
                  src={formData.verifiedBy.signature}
                  alt="Verified By Signature"
                  className="mt-2 max-w-full h-16 border border-gray-200 rounded"
                />
              )}
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <label className="block text-xs font-medium text-gray-700 mb-2">Site Manager (Contractor)</label>
              <input
                type="text"
                value={formData.siteManagerContractor.name}
                onChange={(e) => handleSignatureChange('siteManagerContractor', 'name', e.target.value)}
                placeholder="Enter name"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white mb-2"
              />
              <button
                onClick={() => handleSignatureClick('siteManagerContractor')}
                className="w-full px-3 py-2 bg-[#F2F8FC] hover:bg-[#E3F0F9] text-[#1C4D88] rounded-lg text-xs font-medium transition-colors border border-[#C2DFF1]"
              >
                {formData.siteManagerContractor.signature ? 'Change Signature' : 'Add Signature'}
              </button>
              {formData.siteManagerContractor.signature && (
                <img
                  src={formData.siteManagerContractor.signature}
                  alt="Site Manager Contractor Signature"
                  className="mt-2 max-w-full h-16 border border-gray-200 rounded"
                />
              )}
            </div>
            <div className="border border-gray-200 rounded-lg p-3">
              <label className="block text-xs font-medium text-gray-700 mb-2">Site Manager (Owner)</label>
              <input
                type="text"
                value={formData.siteManagerOwner.name}
                onChange={(e) => handleSignatureChange('siteManagerOwner', 'name', e.target.value)}
                placeholder="Enter name"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white mb-2"
              />
              <button
                onClick={() => handleSignatureClick('siteManagerOwner')}
                className="w-full px-3 py-2 bg-[#F2F8FC] hover:bg-[#E3F0F9] text-[#1C4D88] rounded-lg text-xs font-medium transition-colors border border-[#C2DFF1]"
              >
                {formData.siteManagerOwner.signature ? 'Change Signature' : 'Add Signature'}
              </button>
              {formData.siteManagerOwner.signature && (
                <img
                  src={formData.siteManagerOwner.signature}
                  alt="Site Manager Owner Signature"
                  className="mt-2 max-w-full h-16 border border-gray-200 rounded"
                />
              )}
            </div>
          </div>
        </div>

        {/* Signature Drawing Canvas Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-20">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Draw Signature</h2>
          <div className="space-y-4">
            <div className="flex items-center gap-4 mb-3">
              <label className="text-sm font-medium text-gray-700">Select Signature Field:</label>
              <select
                value={activeSignatureField || ''}
                onChange={(e) => setActiveSignatureField(e.target.value as any)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] bg-white cursor-pointer"
              >
                <option value="">-- Select Field --</option>
                <option value="checkBy">Check By</option>
                <option value="verifiedBy">Verified By</option>
                <option value="siteManagerContractor">Site Manager (Contractor)</option>
                <option value="siteManagerOwner">Site Manager (Owner)</option>
              </select>
              {activeSignatureField && (
                <span className="text-sm text-gray-600">
                  Drawing for: <strong>{activeSignatureField === 'checkBy' ? 'Check By' : 
                                  activeSignatureField === 'verifiedBy' ? 'Verified By' :
                                  activeSignatureField === 'siteManagerContractor' ? 'Site Manager (Contractor)' :
                                  'Site Manager (Owner)'}</strong>
                </span>
              )}
            </div>
            
            {activeSignatureField ? (
              <div className="space-y-4">
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="w-full border-2 border-gray-300 rounded-lg cursor-crosshair bg-white"
                  style={{ height: '200px', touchAction: 'none' }}
                />
                <div className="flex gap-3">
                  <button
                    onClick={clearSignature}
                    className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    <Trash2 size={16} />
                    Clear Canvas
                  </button>
                  <button
                    onClick={saveSignature}
                    className="flex items-center gap-1.5 px-4 py-2 bg-[#0066A3] hover:bg-[#1C4D88] text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <Check size={16} />
                    Save to {activeSignatureField === 'checkBy' ? 'Check By' : 
                            activeSignatureField === 'verifiedBy' ? 'Verified By' :
                            activeSignatureField === 'siteManagerContractor' ? 'Site Manager (Contractor)' :
                            'Site Manager (Owner)'}
                  </button>
                </div>
                <p className="text-sm text-gray-500 bg-[#F2F8FC] p-3 rounded-lg border border-[#C2DFF1]">
                  <strong>Instructions:</strong> Draw your signature above using your mouse or touchscreen. 
                  Select the signature field first, then draw and save your signature.
                </p>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 bg-gray-50 text-center">
                <Pen size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-lg text-gray-600 mb-2">Select a signature field to start drawing</p>
                <p className="text-sm text-gray-500">
                  Choose which signature field you want to add a signature to from the dropdown above
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Signature Modal */}
        {activeSignatureField && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add Signature - {activeSignatureField === 'checkBy' ? 'Check By' : 
                                  activeSignatureField === 'verifiedBy' ? 'Verified By' :
                                  activeSignatureField === 'siteManagerContractor' ? 'Site Manager (Contractor)' :
                                  'Site Manager (Owner)'}
                  </h3>
                  <button
                    onClick={closeSignatureModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={24} />
                  </button>
                </div>

                {/* Signature Mode Tabs */}
                <div className="flex gap-2 mb-4 border-b border-gray-200">
                  <button
                    onClick={() => setSignatureMode('draw')}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                      signatureMode === 'draw'
                        ? 'text-[#0066A3] border-b-2 border-[#0066A3]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Pen size={14} />
                    Draw Signature
                  </button>
                  <button
                    onClick={() => setSignatureMode('upload')}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                      signatureMode === 'upload'
                        ? 'text-[#0066A3] border-b-2 border-[#0066A3]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Upload size={14} />
                    Upload Image
                  </button>
                </div>

                {/* Signature Canvas/Upload Area */}
                {signatureMode === 'draw' ? (
                  <div className="space-y-4">
                    <canvas
                      ref={canvasRef}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      className="w-full border border-gray-300 rounded-lg cursor-crosshair bg-white"
                      style={{ height: '200px', touchAction: 'none' }}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={clearSignature}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-xs font-medium transition-colors"
                      >
                        <Trash2 size={14} />
                        Clear
                      </button>
                      <button
                        onClick={saveSignature}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0066A3] hover:bg-[#1C4D88] text-white rounded text-xs font-medium transition-colors"
                      >
                        <Check size={14} />
                        Save Signature
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Draw your signature above using your mouse or touchscreen
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <input
                      ref={signatureFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <div className="border border-gray-300 rounded-lg p-8 bg-gray-50 text-center">
                      <Upload size={32} className="mx-auto text-gray-400 mb-4" />
                      <p className="text-sm text-gray-600 mb-4">
                        Click below to upload a signature image
                      </p>
                      <button
                        onClick={() => signatureFileInputRef.current?.click()}
                        className="px-4 py-2 bg-[#0066A3] hover:bg-[#1C4D88] text-white rounded text-sm font-medium transition-colors"
                      >
                        Choose Image
                      </button>
                      <p className="text-xs text-gray-500 mt-2">
                        Supported formats: JPG, PNG, GIF (Max 5MB)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InverterCheckSheet;
